import type { useGT } from 'gt-react';
import type { OverrideOptionInput } from '@erp/rpc';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { buildBoardContractPriceOverrideInput } from '../components/boardContractPriceApply';
import { costAgreementIdForContractNumber } from './boardContractNumber';
import type { BulkOverrideLineResult } from './bulkFulfillmentOverride';
import { executePlannedBulkOverrideMutations } from './bulkOverrideApply';

type Gt = ReturnType<typeof useGT>;

export function planContractPriceOverrideForLine(
  line: AssignmentLine,
  supplierId: string,
  contractNumber: number,
): BulkOverrideLineResult {
  const costAgreementId = costAgreementIdForContractNumber(
    line,
    contractNumber,
  );
  if (costAgreementId == null) {
    return {
      suggestionId: line.suggestionId,
      status: 'skipped',
      reason: 'contract_unavailable',
    };
  }

  const input = buildBoardContractPriceOverrideInput({
    line,
    supplierId,
    costAgreementId,
  });
  if (input == null) {
    if (line.status !== 'pending') {
      return {
        suggestionId: line.suggestionId,
        status: 'skipped',
        reason: line.status === 'rejected' ? 'trashed' : 'released',
      };
    }
    return {
      suggestionId: line.suggestionId,
      status: 'skipped',
      reason: 'contract_not_applicable',
    };
  }

  return { suggestionId: line.suggestionId, status: 'applied', input };
}

export function planBulkContractPriceOverrides(
  lines: readonly AssignmentLine[],
  supplierId: string,
  contractNumber: number,
): BulkOverrideLineResult[] {
  return lines.map((line) =>
    planContractPriceOverrideForLine(line, supplierId, contractNumber),
  );
}

export async function applyBulkContractPriceOverrides(
  gt: Gt,
  lines: readonly AssignmentLine[],
  bulkLineCount: number,
  supplierId: string,
  contractNumber: number,
  mutateAsync: (input: OverrideOptionInput) => Promise<unknown>,
): Promise<{ applied: number; skipped: number; failed: number }> {
  const planned = planBulkContractPriceOverrides(
    lines,
    supplierId,
    contractNumber,
  );
  return executePlannedBulkOverrideMutations(
    gt,
    bulkLineCount,
    lines,
    planned,
    mutateAsync,
  );
}
